import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { API } from '../../api';
import type { TeamsConfig } from '../../types/teams';
import DosenButton from '../ui/Buttons/DosenButton';
import { MOBILE_SX, useIsMobileApp } from '../../mobile/mobileApp';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
  orgId: string;
}

type Step = 'instructions' | 'credentials' | 'connecting' | 'result';

const EMPTY_CONFIG: TeamsConfig = {
  tenantId: '',
  clientId: '',
  clientSecret: '',
  catalogAppId: ''
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#1A1A1A',
    borderRadius: '6px',
    color: '#FDFDFD',
    fontSize: 14,
    '& fieldset': { borderColor: '#444444' },
    '&:hover fieldset': { borderColor: '#868686' },
    '&.Mui-focused fieldset': { borderColor: '#868686' }
  },
  '& .MuiInputLabel-root': { color: '#868686', fontSize: 14 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#B6B6B6' },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: '#868686' }
};

export default function TeamsConnectModal({ isOpen, onClose, onConnected, orgId }: Props) {
  const [step, setStep] = useState<Step>('instructions');
  // A phone: the whole screen, scrolling, so the fields and buttons below the steps stay reachable.
  const isMobileApp = useIsMobileApp();
  const [config, setConfig] = useState<TeamsConfig>(EMPTY_CONFIG);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ matchedUserCount?: number; totalUserCount?: number } | null>(null);

  const handleClose = () => {
    setStep('instructions');
    setConfig(EMPTY_CONFIG);
    setShowSecret(false);
    setError(null);
    setResult(null);
    onClose();
  };

  const handleFieldChange = (field: keyof TeamsConfig) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const isFormFilledEnough =
    config.tenantId.trim() !== '' &&
    config.clientId.trim() !== '' &&
    config.clientSecret.trim() !== '';

  const handleConnect = async () => {
    setStep('connecting');
    setError(null);
    try {
      const res = await API.connectTeams(orgId, {
        tenantId: config.tenantId.trim(),
        clientId: config.clientId.trim(),
        clientSecret: config.clientSecret.trim(),
        ...(config.catalogAppId?.trim() ? { catalogAppId: config.catalogAppId.trim() } : {})
      });
      setResult({ matchedUserCount: res.matchedUserCount, totalUserCount: res.totalUserCount });
      setStep('result');
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          'Connection failed. Check the credentials and that an admin consented the required Graph permissions.'
      );
      setStep('credentials');
    }
  };

  const handleDone = () => {
    onConnected();
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobileApp}
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 550,
          [MOBILE_SX]: {
            m: 0,
            width: '100%',
            maxWidth: '100%',
            border: 'none',
            borderRadius: 0,
            overflowY: 'auto',
            overflowWrap: 'anywhere'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
          <TeamsIcon size={22} />
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700 }}>
            Connect Microsoft Teams
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {step === 'instructions' && (
          <>
            <Typography sx={{ color: '#B6B6B6', fontSize: 14, lineHeight: '22px' }}>
              To deliver Dosen notifications and Elliott in Teams, your IT department registers an Azure AD app
              and grants it two Graph permissions. This only needs to be done once.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: '6px',
                background: '#1A1A1A',
                border: '1px solid #2A2A2A',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                What your IT team needs to do:
              </Typography>
              {[
                'Register a new app in Azure Active Directory',
                'Grant User.Read.All and TeamsAppInstallation.ReadWriteForUser.All (Application permissions) and grant admin consent',
                'Create a client secret and note the Tenant ID, Client ID, and Client Secret',
                'Publish the Dosen app into your Teams app catalog (optional, enables proactive install)'
              ].map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: '1px'
                    }}
                  >
                    <Typography sx={{ color: '#B6B6B6', fontSize: 11, fontWeight: 700 }}>{i + 1}</Typography>
                  </Box>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 13, lineHeight: '20px' }}>{s}</Typography>
                </Box>
              ))}
            </Box>

            <Box
              component="a"
              href="https://docs.dosen.io/integrations/teams"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#B6B6B6',
                fontSize: 13,
                textDecoration: 'none',
                '&:hover': { color: '#FDFDFD' }
              }}
            >
              View the full setup guide
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <DosenButton onClick={() => setStep('credentials')} sizeVariant="sm">
                Continue to Connect
              </DosenButton>
            </Box>
          </>
        )}

        {step === 'credentials' && (
          <>
            <TextField
              label="Tenant ID"
              fullWidth
              value={config.tenantId}
              onChange={handleFieldChange('tenantId')}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Client ID"
              fullWidth
              value={config.clientId}
              onChange={handleFieldChange('clientId')}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Client Secret"
              fullWidth
              value={config.clientSecret}
              onChange={handleFieldChange('clientSecret')}
              type={showSecret ? 'text' : 'password'}
              size="small"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSecret((v) => !v)}
                      edge="end"
                      sx={{ color: '#868686', p: 0.5 }}
                    >
                      {showSecret ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Teams App Catalog ID (optional)"
              fullWidth
              value={config.catalogAppId ?? ''}
              onChange={handleFieldChange('catalogAppId')}
              size="small"
              placeholder="e.g. 5d81032d-27ee-4fe9-a267-741fafa26553"
              sx={inputSx}
            />

            {error && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <ErrorOutlineIcon sx={{ color: '#F44336', fontSize: 18, mt: '1px' }} />
                <Typography sx={{ color: '#F44336', fontSize: 13, lineHeight: '20px' }}>{error}</Typography>
              </Box>
            )}

            <Typography sx={{ color: '#868686', fontSize: 12, lineHeight: '18px' }}>
              Connecting matches your people to Microsoft 365 by email. This may take a moment for large organizations.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
              <DosenButton onClick={() => setStep('instructions')} tone="basic" sizeVariant="sm">
                Previous
              </DosenButton>
              <DosenButton onClick={handleConnect} disabled={!isFormFilledEnough} sizeVariant="sm">
                Connect
              </DosenButton>
            </Box>
          </>
        )}

        {step === 'connecting' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={32} sx={{ color: '#FDFDFD' }} />
            <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>
              Connecting and matching your people...
            </Typography>
          </Box>
        )}

        {step === 'result' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 40 }} />
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600 }}>
              Microsoft Teams connected
            </Typography>
            <Typography sx={{ color: '#B6B6B6', fontSize: 14, textAlign: 'center', lineHeight: '21px' }}>
              {result?.totalUserCount !== undefined
                ? `${result?.matchedUserCount ?? 0} of ${
                    result?.totalUserCount
                  } people matched to Microsoft 365. Matched people will receive notifications in Teams.`
                : 'People will receive notifications in Teams.'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <DosenButton onClick={handleDone} sizeVariant="sm">
                Done
              </DosenButton>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

function TeamsIcon({ size = 20 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="3" fill="#5059C9" />
        <circle cx="13.5" cy="6" r="2.1" fill="#fff" fillOpacity="0.95" />
        <rect x="9.2" y="8.4" width="7.6" height="6.2" rx="1.4" fill="#fff" fillOpacity="0.85" />
        <circle cx="7" cy="6.6" r="2.6" fill="#fff" fillOpacity="0.6" />
        <rect x="2.6" y="9.2" width="6.4" height="6.2" rx="1.4" fill="#fff" fillOpacity="0.6" />
      </svg>
    </Box>
  );
}
